import { lazy, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import Loadable from '../components/Loadable';
import RouteGuard from '../components/RouteGuard/RouteGuard';
import { AUTHENTICATOR_OTP, EMAIL_OTP } from '../config/constants';
import AuthLayout from '../layouts/auth/AuthLayout';
import { IAuthState } from '../stateManagement/reducers/authReducer';
import { IRootReducerT } from '../stateManagement/reduxStore';

interface IPageTemplateProps {
    altRoute: string;
    page: ReactNode;
    pageName: string;
}

const PageTemplate: React.FC<IPageTemplateProps> = ({ pageName, altRoute, page }) => {

    const { authToken, user } = useSelector<IRootReducerT, IAuthState>(state => state.auth);
    let conditions: boolean[] = [];
    const hasValidAuthToken = (authToken !== null);
    const isTime4Email2FA = hasValidAuthToken && (user?.usesEmail2FA === true) && (user.email2FADone === false);
    const isTime4Authenticator2FA = hasValidAuthToken && !isTime4Email2FA && (user?.usesAuthenticator2FA === true) && (user.authenticator2FADone === false);

    switch (pageName) {
        case "validate-email-2fa-otp":
            conditions = [ Boolean(user), isTime4Email2FA];
            break;
        case "validate-authenticator-2fa-otp":
            conditions = [Boolean(user), isTime4Authenticator2FA];
            break;
        default:
            conditions = [!hasValidAuthToken]     //User must be unauthenticated to access this route
            break;
    }

    return (
        <>
            <RouteGuard conditions={conditions} altRoute={altRoute} />
            {page}
        </>
    );
}

//Lazy pages
const LLogin = Loadable(lazy(() => import('../pages/auth/Login')));
const LSignUp = Loadable(lazy(() => import('../pages/auth/SignUp')));
const LResetPasswordRequest = Loadable(lazy(() => import('../pages/auth/ResetPasswordRequest')));
const LResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const LSetupAuthenticator = Loadable(lazy(() => import('../pages/auth/SetupAuthenticator')));
const LValidate2FAOTP = Loadable(lazy(() => import('../pages/auth/Validate2FAOTP')));

const AuthRoutes = {
    path: '/',
    element: <AuthLayout />,
    children: [
        {
            path: 'sign-up',
            element: <PageTemplate pageName='sign-up' altRoute='/' page={<LSignUp />} />
        },
        {
            path: 'setup-authenticator',
            element: <PageTemplate pageName='setup-authenticator' altRoute='/' page={<LSetupAuthenticator />} />
        },
        {
            path: 'login',
            element: <PageTemplate pageName='login' altRoute='/validate-email-2fa-otp' page={<LLogin />} />,
        },
        {
            path: 'validate-email-2fa-otp',
            element: <PageTemplate pageName='validate-email-2fa-otp' altRoute='/validate-authenticator-2fa-otp' page={<LValidate2FAOTP type={EMAIL_OTP} />} />
        },
        {
            path: 'validate-authenticator-2fa-otp',
            element: <PageTemplate pageName='validate-authenticator-2fa-otp' altRoute='/' page={<LValidate2FAOTP type={AUTHENTICATOR_OTP} />} />
        },
        {
            path: 'reset-password-request',
            element: <PageTemplate pageName='reset-password-request' altRoute='/' page={<LResetPasswordRequest />} />,
        },
        {
            path: 'reset-password/:emailToken',
            element: <PageTemplate pageName='reset-password' altRoute='/' page={<LResetPassword />} />
        },
    ]
};

export default AuthRoutes;